import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress, MenuItem } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "components/Button";
import Input from "components/Input";
import RadioInput from "components/RadioInput";
import SelectInput from "components/SelectInput";
import { useProfessional } from "providers/Professional";
import { Gender, MaritalStatus } from "types/enums";
import { BrazilianState } from "types/ibge";
import { Professional } from "types/professional";
import { ProfessionalEditData } from "types/register";
import { getBrazilianStates } from "utils/brazilianStates";
import checkCep from "utils/checkCep";
import { cnpjMask, mobilePhoneMask, phoneMask } from "utils/formMasks";
import { validateBirthDate, validateCpfAndCnpj } from "utils/formValidations";
import {
  handleBirthDateInput,
  handleCepInput,
  handleComercialPhoneInput,
  handleCpfOrCnpjInput,
  handleMobilePhoneInput,
} from "utils/handleInput";

interface ProfessionalEditFormProps {
  onHide: () => void;
  professional: Professional;
  companyId?: number;
}

const ProfessionalEditForm = ({
  onHide,
  professional,
  companyId,
}: ProfessionalEditFormProps) => {
  const [inputCep, setInputCep] = useState("");
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [brazilianStates, setBrazilianStates] = useState<BrazilianState[]>([]);

  const { editProfessional, isLoading } = useProfessional();

  const schema = yup.object().shape({
    id: yup.number().default(professional.id),
    name: yup.string().required("*Campo obrigatório").trim(),
    rqe: yup.string().trim(),
    professionalRegister: yup.string().trim(),
    socialName: yup.string().uppercase().trim(),
    birthDate: yup
      .string()
      .required("*Campo obrigatório")
      .test("birthDateValidator", "*Data inválida", validateBirthDate),
    cpf: yup
      .string()
      .required("*Campo obrigatório")
      .test("cpfOrCnpjValidator", "*CPF ou CNPJ inválido", validateCpfAndCnpj)
      .transform((value) => value.replace(/\D/g, "")),
    rg: yup.string().required("*Campo obrigatório").uppercase().trim(),
    email: yup
      .string()
      .required("*Campo obrigatório")
      .email("*E-mail inválido")
      .trim(),
    comercialPhone: yup.string(),
    mobilePhone: yup
      .string()
      .required("*Campo obrigatório")
      .min(15, "*Número de celular inválido"),
    maritalStatus: yup.string().required(),
    sex: yup.string(),

    addressCEP: yup
      .string()
      .required("*Campo obrigatório")
      .min(8, "*Deve ter 8 dígitos"),
    addressRoad: yup.string().required("*Campo obrigatório").uppercase().trim(),
    addressNumber: yup
      .string()
      .required("*Campo obrigatório")
      .uppercase()
      .trim(),
    addressComplement: yup.string().uppercase().trim(),
    addressDistrict: yup
      .string()
      .required("*Campo obrigatório")
      .uppercase()
      .trim(),
    addressState: yup
      .string()
      .test("selectState", "*Selecione um Estado", (inputValue) =>
        inputValue === "" ? false : true
      ),
    addressCity: yup
      .string()
      .test("selectCity", "*Selecione uma Cidade", (inputValue) =>
        inputValue === "" ? false : true
      )
      .transform((value) => value.toUpperCase()),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    control,
    formState: { errors },
  } = useForm<ProfessionalEditData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: ProfessionalEditData) => {
    editProfessional(data, professional.id, onHide, companyId);
  };

  useEffect(() => {
    getBrazilianStates(setBrazilianStates);
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Body>
        <div className="row">
          <div className="col-xl-6">
            <h5 className="mb-4">Dados do Profissional</h5>
            <div className="form-group">
              <Input
                className="text-uppercase"
                label="Nome Completo:"
                placeholder="Seu Nome Aqui"
                type="text"
                register={register("name")}
                error={errors?.name?.message}
                defaultValue={professional.name}
              />
            </div>
            <div className="row">
              <div className="form-group col-md-6">
                <Input
                  className="text-uppercase"
                  label="Cargo:"
                  placeholder="Seu cargo aqui"
                  type="text"
                  register={register("rqe")}
                  error={errors?.rqe?.message}
                  defaultValue={professional.rqe}
                />
              </div>
              <div className="form-group col-md-6">
                <Input
                  className="text-uppercase"
                  label="Registro profissional:"
                  placeholder="Seu registro profissional aqui"
                  type="text"
                  register={register("professionalRegister")}
                  error={errors?.professionalRegister?.message}
                  defaultValue={professional.professionalRegister}
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group col-md-6">
                <Input
                  className="text-uppercase"
                  label="Nome Social:"
                  placeholder="Como prefere ser chamado?"
                  type="text"
                  register={register("socialName")}
                  error={errors?.socialName?.message}
                  defaultValue={professional.socialName}
                />
              </div>

              <div className="form-group col-md-6">
                <Input
                  label="Data de Nascimento:"
                  placeholder="dd/mm/aaaa"
                  type="tel"
                  maxLength={10}
                  register={register("birthDate")}
                  error={errors?.birthDate?.message}
                  onChange={(e) =>
                    handleBirthDateInput(e, setValue, "birthDate")
                  }
                  defaultValue={moment(professional.birthDate).format(
                    "DD/MM/YYYY"
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <Input
                  label="CPF / CNPJ:"
                  placeholder="CPF ou CNPJ"
                  type="text"
                  maxLength={18}
                  register={register("cpf")}
                  error={errors?.cpf?.message}
                  onChange={(e) => handleCpfOrCnpjInput(e, setValue, "cpf")}
                  defaultValue={cnpjMask(professional.cpf)}
                />
              </div>

              <div className="form-group col-md-6">
                <Input
                  className="text-uppercase"
                  label="RG:"
                  placeholder="9999999-9"
                  type="tel"
                  register={register("rg")}
                  defaultValue={professional.rg}
                  error={errors?.rg?.message}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-12">
                <Input
                  type="text"
                  label="E-mail:"
                  placeholder="instituicao@email.com"
                  register={register("email")}
                  error={errors?.email?.message}
                  defaultValue={professional.email}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <Input
                  label="Celular:"
                  type="tel"
                  maxLength={15}
                  register={register("mobilePhone")}
                  error={errors?.mobilePhone?.message}
                  onChange={(e) =>
                    handleMobilePhoneInput(e, setValue, "mobilePhone")
                  }
                  defaultValue={mobilePhoneMask(
                    `${professional.dddMobilePhone}${professional.mobilePhone}`
                  )}
                />
              </div>
              <div className="form-group col-md-6">
                <Input
                  label="Celular Comercial:"
                  type="tel"
                  maxLength={15}
                  register={register("comercialPhone")}
                  error={errors?.comercialPhone?.message}
                  onChange={(e) =>
                    handleComercialPhoneInput(e, setValue, "comercialPhone")
                  }
                  defaultValue={phoneMask(
                    `${professional.dddComercialPhone}${professional.comercialPhone}`
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <div className="form-group">
                  <SelectInput
                    label="Estado civil:"
                    placeholder="Estado Civil"
                    control={control}
                    name="maritalStatus"
                    defaultValue={
                      professional.maritalStatus ??
                      MaritalStatus["NÃO INFORMADO"]
                    }
                    error={errors?.maritalStatus?.message}
                  >
                    <MenuItem value={MaritalStatus.Solteiro}>SOLTEIRO</MenuItem>
                    <MenuItem value={MaritalStatus.Casado}>CASADO</MenuItem>
                    <MenuItem value={MaritalStatus.Separado}>SEPARADO</MenuItem>
                    <MenuItem value={MaritalStatus.Divorciado}>
                      DIVORCIADO
                    </MenuItem>
                    <MenuItem value={MaritalStatus.Viuvo}>VIÚVO</MenuItem>
                    <MenuItem value={MaritalStatus["NÃO INFORMADO"]}>
                      NÃO INFORMADO
                    </MenuItem>
                  </SelectInput>
                </div>
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="select">Sexo</label>
                <div className="row">
                  <div className="col col-auto">
                    <RadioInput
                      value="M"
                      label="Masculino"
                      register={register("sex")}
                      defaultChecked={professional?.sex === Gender.Male}
                    />
                  </div>
                  <div className="col col-auto">
                    <RadioInput
                      value="F"
                      label="Feminino"
                      register={register("sex")}
                      defaultChecked={professional?.sex === Gender.Female}
                    />
                  </div>
                  <div className="col col-auto">
                    <RadioInput
                      value="I"
                      label="Intersexo"
                      register={register("sex")}
                      defaultChecked={professional?.sex === Gender.Intersex}
                    />
                  </div>
                </div>
                <div className="text-danger mb-3">
                  <small>{!!errors?.sex && "*Selecione uma opção"}</small>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-6">
            <h5 className="mt-2 mt-xl-0 mb-4">Endereço</h5>

            <div className="form-group">
              <div className="row flex-column gap-2 flex-sm-row gap-md-0 align-items-center">
                <div className="col">
                  <Input
                    label="Consulte pelo CEP:"
                    placeholder="99999-999"
                    type="text"
                    register={register("addressCEP")}
                    error={errors?.addressCEP?.message}
                    onChange={(e) =>
                      handleCepInput(e, setValue, "addressCEP", setInputCep)
                    }
                    defaultValue={professional.addressCEP}
                  />
                  <div className="col-12 col-md-auto small ps-0">
                    <a
                      href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Não sei meu CEP!
                    </a>
                  </div>
                </div>

                <div className="col col-auto">
                  <Button
                    type="button"
                    className="btn-sm btn-dark mt-3"
                    onClick={() =>
                      checkCep(inputCep, setIsSearchingCep, setValue, setFocus)
                    }
                    disabled={isSearchingCep}
                  >
                    Consultar
                    {!!isSearchingCep && (
                      <CircularProgress
                        color="inherit"
                        size={12}
                        className="align-text-top ms-2"
                      />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-8">
                <div className="form-group">
                  <Input
                    label="Endereço:"
                    placeholder="Nome da Rua"
                    type="text"
                    register={register("addressRoad")}
                    error={errors?.addressRoad?.message}
                    defaultValue={professional.addressRoad}
                  />
                </div>
              </div>

              <div className="col-sm-4">
                <div className="form-group">
                  <Input
                    label="Número:"
                    placeholder="nº"
                    type="text"
                    register={register("addressNumber")}
                    error={errors?.addressNumber?.message}
                    defaultValue={professional.addressNumber}
                  />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="form-group">
                  <Input
                    label="Complemento:"
                    placeholder="Casa, apto, bloco"
                    type="text"
                    register={register("addressComplement")}
                    error={errors?.addressComplement?.message}
                    defaultValue={professional.addressComplement}
                  />
                </div>
              </div>

              <div className="col-sm-6">
                <Input
                  label="Bairro:"
                  placeholder="Nome do Bairro"
                  type="text"
                  register={register("addressDistrict")}
                  error={errors?.addressDistrict?.message}
                  defaultValue={professional.addressDistrict}
                />
              </div>

              <div className="col-sm-6">
                <SelectInput
                  name="addressState"
                  label="Estado:"
                  placeholder="Selecione"
                  control={control}
                  error={errors?.addressState?.message}
                  defaultValue={professional.addressState ?? "selecione"}
                >
                  <MenuItem value="selecione" disabled>
                    Selecione
                  </MenuItem>
                  {brazilianStates?.map((brazilianState) => (
                    <MenuItem
                      key={brazilianState.id}
                      value={brazilianState.sigla}
                    >
                      {brazilianState.sigla}
                    </MenuItem>
                  ))}
                </SelectInput>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <Input
                    label="Cidade:"
                    placeholder="Nome da Cidade"
                    type="text"
                    register={register("addressCity")}
                    error={errors?.addressCity?.message}
                    defaultValue={professional.addressCity}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          className="btn-light"
          onClick={onHide}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button type="submit" className="btn-primary" disabled={isLoading}>
          <i className="uil uil-check mr-2" />
          Salvar
          {!!isLoading && (
            <CircularProgress
              color="inherit"
              size={12}
              className="align-text-top ms-2"
            />
          )}
        </Button>
      </Modal.Footer>
    </form>
  );
};

export default ProfessionalEditForm;
